import { ReactElement, useMemo } from 'react';

import { useFormContext } from 'core/states';
import { ShipmentTypeFormField } from 'corporate/elements';
import { ICustomerQuotePart, IQuotePart } from 'quotes/types';

import { CostFormFields, CostFormFieldsProvider, usePricingStateContext } from './CostFormFields';
import {
  CorporateLocationFormField,
  ShippingFieldsChangeWarning,
  VendorFormField,
} from './ShippingFormFields';

interface IProps {
  quotePart: IQuotePart;
  quoteId?: number;
  isAlternateVersionEnabled: boolean;
  isShipAndDebitVendor: boolean;
  externalReplacementCost?: number;
  externalRepCostRegistrationNum?: string;
  shipmentTypeRequestPayload?: Record<string, unknown>;
  corporateLocationHelperTexts?: string[] | ReactElement[];
}

export const CostSection = ({
  quotePart,
  quoteId,
  isAlternateVersionEnabled,
  isShipAndDebitVendor,
  externalReplacementCost,
  externalRepCostRegistrationNum,
  shipmentTypeRequestPayload,
  corporateLocationHelperTexts,
}: IProps) => {
  const { watch } = useFormContext();

  const { disablePricingFields, onChangeShipmentType, onChangeCorporateLocation } =
    usePricingStateContext();

  const partValue = watch('part');

  const defaultShipmentTypeId = useMemo(
    () => Number((quotePart as ICustomerQuotePart)?.shipmentType?.id) || null,
    [quotePart]
  );

  const defaultCorporateLocationId = useMemo(
    () => Number((quotePart as ICustomerQuotePart)?.corporateLocation?.id) || null,
    [quotePart]
  );

  return (
    <CostFormFieldsProvider
      isShipAndDebitVendor={isShipAndDebitVendor}
      externalReplacementCost={externalReplacementCost}
      externalRepCostRegistrationNum={externalRepCostRegistrationNum}
      quoteId={quoteId}
      quotePart={quotePart as ICustomerQuotePart}
    >
      <ShippingFieldsChangeWarning
        initialValues={{
          shipmentTypeId: defaultShipmentTypeId,
          corporateLocationId: defaultCorporateLocationId,
        }}
        currentValues={{
          shipmentTypeId: Number(watch('shipmentType')?.id),
          corporateLocationId: Number(watch('corporateLocation')?.id),
        }}
      />

      <ShipmentTypeFormField
        name="shipmentType"
        onChange={onChangeShipmentType}
        requestPayload={shipmentTypeRequestPayload || { filters: { deleted: 0 } }}
        disabled={disablePricingFields}
      />

      <CorporateLocationFormField
        name="corporateLocation"
        quotePart={quotePart as ICustomerQuotePart}
        onChange={onChangeCorporateLocation}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        disabled={disablePricingFields}
        helperTexts={corporateLocationHelperTexts}
      />

      {isAlternateVersionEnabled && (
        <VendorFormField
          externalSupplier={partValue?.supplier || quotePart?.supplier || null}
          disabled={disablePricingFields}
          level={1}
        />
      )}

      <CostFormFields />
    </CostFormFieldsProvider>
  );
};
